import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { AddBillEmailVariantModal } from "@features/AddBillEmailVariantModal/AddBillEmailVariantModal";
import { AddBillModal } from "@features/AddBillModal/AddBillModal";
import { AddBillPdfMethodModal } from "@features/AddBillPdfMethodModal/AddBillPdfMethodModal";
import { PowerOfAttorneyAddFromBudgetConsentModal } from "@features/PowerOfAttorneyAddFromBudgetConsentModal/PowerOfAttorneyAddFromBudgetConsentModal";
import { Button } from "@components/Button";
import { ExpandingContainer } from "@components/ExpandingContainer/ExpandingContainer";
import { DocumentIcon, LetterIcon, ReceiptIcon } from "@components/icons";
import { Modal } from "@components/Modal/Modal";
import { useAddBillModal } from "@hooks/useAddBillModal";
import { useFeatureConfig } from "@hooks/useFeatureFlags";
import { useModal } from "@hooks/useModal";
import { usePOAModal } from "@hooks/usePOAModal";
import { useModalStore, type IUseModalProps } from "@store/modal/modal";
import { useGetPOAStatementConsentsBankLinkMutation } from "@services/powersOfAttorney/mutations";
import {
  usePOASignedDocumentMetadataQuery,
  useUnderpermissionedExistingConsentsQuery,
} from "@services/powersOfAttorney/queries";
import { COUNTRY_CODE } from "@/constants";
import { CountryCode } from "@/i18n";
import { AddBillMethod } from "./AddBillVariantSelectorModalTypes";
import { AddBillBankMethodCard } from "./components/AddBillBankMethodCard";
import { AddBillVariantMethodButtonEE } from "./components/AddBillVariantMethodButtonEE";
import { AddBillVariantMethodButtonPL } from "./components/AddBillVariantMethodButtonPL";

export const AddBillVariantSelectorModal: React.FC<IUseModalProps> = (
  props,
) => {
  const modal = useModal(props);
  const { create } = useModalStore();
  const { openPOAModal } = usePOAModal();
  const { openAddBillModal } = useAddBillModal();
  const { t } = useTranslation();
  const { isPoaEnabled } = useFeatureConfig();
  const [selectedMethod, setSelectedMethod] = useState<AddBillMethod | null>(
    AddBillMethod.BANK,
  );

  const [selectedBankId, setSelectedBankId] = useState<number | undefined>(
    undefined,
  );

  const {
    data: underpermissionedConsents,
    isLoading: isUnderpermissionedConsentsLoading,
  } = useUnderpermissionedExistingConsentsQuery(isPoaEnabled);
  const { data: POAdocumentMetadata, isLoading: isPOALoading } =
    usePOASignedDocumentMetadataQuery(isPoaEnabled);
  const { mutateAsync: getPOAConsentLink, isPending } =
    useGetPOAStatementConsentsBankLinkMutation();

  const onMethodSelect = (method: AddBillMethod) => {
    setSelectedBankId(undefined);
    setSelectedMethod(method);
  };

  const handleBankSubmit = async (bankId: number) => {
    const underpermisionedBankConsents =
      underpermissionedConsents?.banks.filter(
        (bank) => bank.bankId === selectedBankId,
      );

    if (underpermisionedBankConsents?.length) {
      return create((modalProps) => (
        <PowerOfAttorneyAddFromBudgetConsentModal
          {...modalProps}
          consents={{ banks: underpermisionedBankConsents }}
          onSuccess={(powerOfAttorneyLedgerConsentId) => {
            openPOAModal({
              powerOfAttorneyLedgerConsentId,
            });
            modal.close();
          }}
        />
      ));
    }

    const { redirectUrl } = await getPOAConsentLink({
      bankId: bankId,
    });

    window.location.replace(redirectUrl);
  };

  const onSubmit = async (method?: AddBillMethod) => {
    if (isPoaEnabled && (isPOALoading || isUnderpermissionedConsentsLoading)) {
      return;
    }

    switch (method ?? selectedMethod) {
      case AddBillMethod.BANK:
        if (selectedBankId) {
          return handleBankSubmit(selectedBankId);
        } else if (POAdocumentMetadata?.succeeded) {
          openPOAModal({ showExistingConsent: true });
          modal.close();
        }
        break;
      case AddBillMethod.EMAIL:
        create((modalProps) => (
          <AddBillEmailVariantModal
            {...modalProps}
            onCancel={() => openAddBillModal()}
          />
        ));
        modal.close();
        break;
      case AddBillMethod.MANUAL:
        create((modalProps) => (
          <AddBillModal {...modalProps} onCancel={() => openAddBillModal()} />
        ));
        modal.close();
        break;
      case AddBillMethod.PDF:
        create((modalProps) => (
          <AddBillPdfMethodModal
            {...modalProps}
            onCancel={() => openAddBillModal()}
          />
        ));
        modal.close();
        break;
      default:
        if (selectedBankId) {
          return handleBankSubmit(selectedBankId);
        }
        break;
    }
  };

  if (COUNTRY_CODE === CountryCode.PL) {
    return (
      <Modal
        modal={modal}
        title={t("addBillVariantSelectorModal.title")}
        subtitle={t("addBillVariantSelectorModal.subtitle")}
        className="md:max-w-3xl"
      >
        <div className="flex flex-col gap-10 pt-12">
          <div className="flex flex-col gap-3">
            <span className="text-sm font-semibold">
              {t("addBillVariantSelectorModal.oneTimeMethods.label")}
            </span>
            <AddBillVariantMethodButtonPL
              badgeIcon={DocumentIcon}
              badgeColor="yellow"
              variant={AddBillMethod.PDF}
              selectedMethod={selectedMethod}
              onClick={() => onSubmit(AddBillMethod.PDF)}
            />
            <AddBillVariantMethodButtonPL
              badgeIcon={ReceiptIcon}
              badgeColor="green"
              variant={AddBillMethod.MANUAL}
              selectedMethod={selectedMethod}
              onClick={() => onSubmit(AddBillMethod.MANUAL)}
            />
          </div>
          <div className="flex flex-col gap-3">
            <span className="text-sm font-semibold">
              {t("addBillVariantSelectorModal.cyclicalMethods.label")}
            </span>
            <AddBillVariantMethodButtonPL
              badgeIcon={LetterIcon}
              badgeColor="purple"
              variant={AddBillMethod.EMAIL}
              selectedMethod={selectedMethod}
              onClick={() => onSubmit(AddBillMethod.EMAIL)}
            />
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      modal={modal}
      title={t("addBillVariantSelectorModal.title")}
      subtitle={t("addBillVariantSelectorModal.subtitle")}
      className="md:max-w-3xl"
      actionButtons={
        <React.Fragment>
          <Button variant="secondary" onClick={modal.close} className="w-full">
            {t("common.form.buttons.cancel")}
          </Button>
          <Button
            className="w-full"
            onClick={() => onSubmit()}
            isLoading={isPending}
          >
            {t("common.form.buttons.continue")}
          </Button>
        </React.Fragment>
      }
    >
      <AddBillBankMethodCard
        variant={AddBillMethod.BANK}
        selectedMethod={selectedMethod}
        onClick={() => onMethodSelect(AddBillMethod.BANK)}
        selectedBankId={selectedBankId}
        setSelectedBankId={setSelectedBankId}
        setBillMethod={setSelectedMethod}
      />

      <ExpandingContainer
        isEnabled
        defaultOpen={POAdocumentMetadata?.succeeded}
        buttonLabel={t("addBillVariantSelectorModal.otherMethodsTitle")}
        className="pt-10"
      >
        <AddBillVariantMethodButtonEE
          badgeIcon={LetterIcon}
          badgeColor="purple"
          variant={AddBillMethod.EMAIL}
          selectedMethod={selectedMethod}
          onClick={() => onMethodSelect(AddBillMethod.EMAIL)}
        />
        <AddBillVariantMethodButtonEE
          badgeIcon={DocumentIcon}
          badgeColor="yellow"
          variant={AddBillMethod.PDF}
          selectedMethod={selectedMethod}
          onClick={() => onMethodSelect(AddBillMethod.PDF)}
        />
        <AddBillVariantMethodButtonEE
          badgeIcon={ReceiptIcon}
          badgeColor="green"
          variant={AddBillMethod.MANUAL}
          selectedMethod={selectedMethod}
          onClick={() => onMethodSelect(AddBillMethod.MANUAL)}
        />
      </ExpandingContainer>
    </Modal>
  );
};
